import * as React from 'react';
import { Cpu, MemoryStick, HardDrive as HardDriveIcon, Clock } from 'lucide-react';
import type { SystemMetricsSnapshot } from '@easyflow/shared';
import { formatBytesHuman, formatUptimeHuman } from '@easyflow/shared';

export type MetricsState =
  | { kind: 'loading' }
  | { kind: 'unsupported' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: SystemMetricsSnapshot; fetchedAt: number };

interface Props {
  state: MetricsState;
}

function pctTone(pct: number): { bar: string; text: string } {
  if (pct >= 90) return { bar: 'bg-rose-500',  text: 'text-rose-600 dark:text-rose-400' };
  if (pct >= 70) return { bar: 'bg-amber-400', text: 'text-amber-600 dark:text-amber-400' };
  return { bar: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' };
}

function MetricBar({ pct }: { pct: number }) {
  const tone = pctTone(pct);
  return (
    <div className="h-1.5 w-full rounded-full bg-slate-200/60 dark:bg-white/10 overflow-hidden">
      <div className={`h-full ${tone.bar} transition-all`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
    </div>
  );
}

function MetricTile({ icon, label, value, sub, pct }: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  pct?: number;
}) {
  const tone = pct !== undefined ? pctTone(pct) : null;
  return (
    <div className="rounded-md bg-white/40 dark:bg-white/5 border border-slate-200/60 dark:border-white/10 p-2.5">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground/70">
        {icon}<span>{label}</span>
      </div>
      <div className="flex items-baseline gap-2 mt-1">
        <span className={`text-base font-semibold ${tone ? tone.text : 'text-foreground'}`}>{value}</span>
        {sub && <span className="text-[11px] text-muted-foreground/70 truncate">{sub}</span>}
      </div>
      {pct !== undefined && <div className="mt-1.5"><MetricBar pct={pct} /></div>}
    </div>
  );
}

function freshness(fetchedAt: number, now: number): string {
  const sec = Math.max(0, Math.round((now - fetchedAt) / 1000));
  if (sec < 5) return 'ora';
  if (sec < 60) return `${sec}s fa`;
  return `${Math.floor(sec / 60)}m fa`;
}

export function MetricsInlinePanel({ state }: Props) {
  const [now, setNow] = React.useState<number>(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (state.kind === 'unsupported') {
    return (
      <div className="text-xs text-muted-foreground/70 italic px-1 py-2">
        Metriche non disponibili — nodo senza collector
      </div>
    );
  }
  if (state.kind === 'loading') {
    return (
      <div className="text-xs text-muted-foreground/70 px-1 py-2">Caricamento metriche…</div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className="text-xs text-rose-600 dark:text-rose-400 px-1 py-2">
        Errore: {state.message}
      </div>
    );
  }

  const { data, fetchedAt } = state;
  const memUsedH = formatBytesHuman(data.memory.used);
  const memTotalH = formatBytesHuman(data.memory.total);
  const diskUsedH = formatBytesHuman(data.disk.used);
  const diskTotalH = formatBytesHuman(data.disk.total);
  const loadStr = data.cpu.loadAvg.map((n) => n.toFixed(2)).join(' · ');

  return (
    <div className="px-1 pt-2 pb-1">
      <div className="grid grid-cols-2 gap-2">
        <MetricTile
          icon={<Cpu className="h-3 w-3" />}
          label={`CPU (${data.cpu.cores} core)`}
          value={`${data.cpu.usage}%`}
          sub={`load ${loadStr}`}
          pct={data.cpu.usage}
        />
        <MetricTile
          icon={<MemoryStick className="h-3 w-3" />}
          label="RAM"
          value={`${data.memory.percentage}%`}
          sub={`${memUsedH} / ${memTotalH}`}
          pct={data.memory.percentage}
        />
        <MetricTile
          icon={<HardDriveIcon className="h-3 w-3" />}
          label={`Disco (${data.disk.mount})`}
          value={`${data.disk.percentage}%`}
          sub={`${diskUsedH} / ${diskTotalH}`}
          pct={data.disk.percentage}
        />
        <MetricTile
          icon={<Clock className="h-3 w-3" />}
          label="Uptime"
          value={formatUptimeHuman(data.uptime)}
          sub={`agg. ${freshness(fetchedAt, now)}`}
        />
      </div>
    </div>
  );
}
